/* ===========================================================
   Myoform — FAQs page

   Scoped entirely to .myo-faqs roots. Handles:
     1. accordion open/close (button + aria-expanded)
     2. smooth anchor scrolling to category sections
     3. active category highlighting in the nav
     4. horizontal nav affordances on mobile

   No external dependencies. One IntersectionObserver and one
   passive scroll listener per section.
   =========================================================== */

(function () {
  'use strict';

  var INITIALISED = 'myoFaqsReady';

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function initAccordion(root) {
    var oneAtATime = root.getAttribute('data-one-at-a-time') === 'true';
    var items = Array.prototype.slice.call(
      root.querySelectorAll('[data-faq-item]')
    );

    function setOpen(item, open) {
      var toggle = item.querySelector('[data-faq-toggle]');
      item.classList.toggle('is-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    // "One at a time" applies within a category group, so opening a
    // question never collapses an answer the reader left open elsewhere.
    function closeSiblings(item) {
      var group = item.closest('.myo-faqs__list') || root;
      Array.prototype.slice
        .call(group.querySelectorAll('[data-faq-item]'))
        .forEach(function (other) {
          if (other !== item) setOpen(other, false);
        });
    }

    function open(item) {
      if (!item) return;
      if (oneAtATime) closeSiblings(item);
      setOpen(item, true);
    }

    items.forEach(function (item) {
      var toggle = item.querySelector('[data-faq-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        if (item.classList.contains('is-open')) {
          setOpen(item, false);
        } else {
          open(item);
        }
      });
    });

    // Lets the Theme Editor reveal the first answer in a selected category.
    root.__myoFaqsOpen = open;
  }

  function initNav(root) {
    var nav = root.querySelector('[data-faq-nav]');
    if (!nav) return;

    var wrap = root.querySelector('[data-faq-nav-wrap]');
    var scroller = root.querySelector('[data-faq-nav-scroller]');
    var links = Array.prototype.slice.call(
      nav.querySelectorAll('[data-faq-nav-link]')
    );
    if (!links.length) return;

    // Anchor IDs are handleized (a-z, 0-9, hyphens), so getElementById is
    // both safe and cheaper than a selector that would need escaping.
    function findTarget(link) {
      var id = (link.getAttribute('href') || '').replace(/^#/, '');
      if (!id) return null;
      var target = document.getElementById(id);
      return target && root.contains(target) ? target : null;
    }

    var targets = [];
    links.forEach(function (link) {
      var target = findTarget(link);
      if (target) targets.push({ link: link, target: target });
    });

    /* --- Publish the nav height so CSS scroll-margin-top can allow
           for it, keeping category headings clear after a jump. --- */
    function publishNavHeight() {
      var isSticky = window.getComputedStyle(nav).position === 'sticky';
      root.style.setProperty(
        '--myo-faqs-nav-h',
        (isSticky ? nav.offsetHeight : 0) + 'px'
      );
    }

    publishNavHeight();

    if ('ResizeObserver' in window) {
      new ResizeObserver(publishNavHeight).observe(nav);
    } else {
      window.addEventListener('resize', publishNavHeight);
    }

    /* --- Smooth scroll. scrollIntoView respects the target's
           scroll-margin-top, so no offset maths is needed here. --- */
    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        var target = findTarget(link);
        if (!target) return;

        event.preventDefault();
        publishNavHeight();
        target.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        });

        // Keep the URL shareable without triggering a second jump.
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', link.getAttribute('href'));
        }
      });
    });

    /* --- Active category tracking. --- */
    function setActive(link) {
      links.forEach(function (other) {
        other.classList.toggle('is-active', other === link);
        if (other === link) {
          other.setAttribute('aria-current', 'true');
        } else {
          other.removeAttribute('aria-current');
        }
      });

      // Bring the active pill into view in the mobile scroller.
      if (link && scroller && scroller.scrollWidth > scroller.clientWidth) {
        var linkBox = link.getBoundingClientRect();
        var boxBox = scroller.getBoundingClientRect();
        if (linkBox.left < boxBox.left || linkBox.right > boxBox.right) {
          scroller.scrollTo({
            left:
              link.offsetLeft - scroller.clientWidth / 2 + link.offsetWidth / 2,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        }
      }
    }

    if (targets.length && 'IntersectionObserver' in window) {
      var visible = {};

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            visible[entry.target.id] = entry.isIntersecting;
          });

          // Highlight the first category currently in the band.
          for (var i = 0; i < targets.length; i++) {
            if (visible[targets[i].target.id]) {
              setActive(targets[i].link);
              return;
            }
          }
        },
        { rootMargin: '-25% 0px -60% 0px', threshold: 0 }
      );

      targets.forEach(function (entry) {
        observer.observe(entry.target);
      });
    }

    /* --- Edge fades telling the user there is more to scroll. --- */
    if (wrap && scroller) {
      var ticking = false;

      function updateFades() {
        var maxScroll = scroller.scrollWidth - scroller.clientWidth;
        wrap.classList.toggle('is-overflow-start', scroller.scrollLeft > 2);
        wrap.classList.toggle(
          'is-overflow-end',
          maxScroll > 2 && scroller.scrollLeft < maxScroll - 2
        );
        ticking = false;
      }

      scroller.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(updateFades);
        },
        { passive: true }
      );

      window.addEventListener('resize', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateFades);
      });

      updateFades();
    }
  }

  function init(root) {
    if (!root || root.dataset[INITIALISED] === 'true') return;
    root.dataset[INITIALISED] = 'true';
    initAccordion(root);
    initNav(root);
  }

  function initAll(scope) {
    var container = scope || document;
    if (container.classList && container.classList.contains('myo-faqs')) {
      init(container);
    }
    Array.prototype.slice
      .call(container.querySelectorAll('.myo-faqs'))
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* --- Theme Editor lifecycle. --- */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest
      ? event.target.closest('.myo-faqs')
      : null;
    if (!root) return;
    init(root);

    if (root.__myoFaqsOpen) {
      root.__myoFaqsOpen(event.target.querySelector('[data-faq-item]'));
    }
  });
})();
